import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { FaFileAlt, FaDownload, FaFilePdf, FaFileWord, FaFileImage } from 'react-icons/fa';

const MyDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents/my-documents');
      
      if (response.data.success) {
        setDocuments(response.data.documents || []);
      } else {
        setDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      
      // Check if it's a 404 or endpoint doesn't exist
      if (error.response?.status === 404) {
        toast.info('Document feature is available - no documents uploaded yet');
      } else {
        toast.error(error.response?.data?.message || 'Failed to fetch documents');
      }
      
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (type) => {
    if (!type) return <FaFileAlt className="text-3xl text-gray-600" />;
    
    const lowerType = type.toLowerCase();
    if (lowerType.includes('pdf')) return <FaFilePdf className="text-3xl text-red-600" />;
    if (lowerType.includes('doc')) return <FaFileWord className="text-3xl text-blue-600" />;
    if (lowerType.includes('image') || lowerType.includes('jpg') || lowerType.includes('png')) {
      return <FaFileImage className="text-3xl text-green-600" />;
    }
    return <FaFileAlt className="text-3xl text-gray-600" />;
  };

  const handleDownload = (doc) => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, '_blank');
    } else {
      toast.error('Document URL not available');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">My Documents</h1>
        <p className="text-gray-600 mt-2">Access your company documents and files</p>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading documents...</p>
        </div>
      ) : documents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FaFileAlt className="text-6xl text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">No Documents Available</h3>
          <p className="text-gray-500">
            Your company documents will appear here once they are uploaded by the admin.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc) => (
            <div key={doc._id} className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                {getFileIcon(doc.type)}
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold mb-1 truncate" title={doc.title}>
                    {doc.title || 'Untitled Document'}
                  </h3>
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {doc.description || 'No description available'}
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Type: {doc.type || 'Unknown'}
                  </p>
                  {doc.uploadedAt && (
                    <p className="text-xs text-gray-400 mb-3">
                      Uploaded: {new Date(doc.uploadedAt).toLocaleDateString()}
                    </p>
                  )}
                  <button
                    onClick={() => handleDownload(doc)}
                    className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <FaDownload /> Download
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && documents.length > 0 && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Total Documents:</strong> {documents.length}
          </p>
        </div>
      )}
    </div>
  );
};

export default MyDocuments;